import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { GoogleMap, MarkerF, Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 }; // London

const AddressUpdateModal = ({ showModal, handleClose, addressData, handleUpdate }) => {
  const [address, setAddress] = useState("");
  const [marker, setMarker] = useState(defaultCenter);
  const [tag, setTag] = useState("");
  const [error, setError] = useState("");
  const [isBouncing, setIsBouncing] = useState(false);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Initialize form when modal opens
  useEffect(() => {
    if (addressData && showModal) {
      setAddress(addressData.address || "");
      setTag(addressData.tag || "");

      if (addressData.latitude && addressData.longitude) {
        setMarker({ lat: addressData.latitude, lng: addressData.longitude });
      } else if (addressData.address) {
        // Geocode if no coordinates
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: addressData.address }, (results, status) => {
            if (status === "OK" && results[0]) {
              const loc = results[0].geometry.location;
              setMarker({ lat: loc.lat(), lng: loc.lng() });
            }
          });
        }
      }
    }
  }, [addressData, showModal]);

  // Reset error when modal closes
  useEffect(() => {
    if (!showModal) setError("");
  }, [showModal]);

  /* ---------------- MAP LOAD ---------------- */
  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  /* ---------------- AUTOCOMPLETE ---------------- */
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.geometry) {
      setError("Please select an address from the list");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setAddress(place.formatted_address);
    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(17);
  };

  /* ---------------- MAP CLICK ---------------- */
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(17);

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) setAddress(results[0].formatted_address);
      });
    }
  };

  /* ---------------- MARKER DRAG ---------------- */
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) setAddress(results[0].formatted_address);
      });
    }
  };

  /* ---------------- SAVE ---------------- */
  const handleAddressUpdate = async () => {
    if (!address.trim()) {
      setError("Please enter a valid address.");
      return;
    }
    setError("");

    try {
      const updatedAddressObject = {
        id: addressData.id,
        address,
        latitude: marker.lat,
        longitude: marker.lng,
        userId: addressData.userId,
        tag,
        NoteForDeliveryMan: "N/A",
        isDefault: addressData.isDefault,
        isLastDeliveryAddress: addressData.isLastDeliveryAddress,
        creationDate: addressData.creationDate,
      };

      await axios.put(`${process.env.REACT_APP_SECRET}/api/DeliveryAddress`, updatedAddressObject);

      handleUpdate(updatedAddressObject);
      handleClose();
    } catch {
      setError("Failed to update address. Please try again.");
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg" enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Address</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoaded && (
          <>
            <Form.Label>Search UK Address:</Form.Label>
            <Autocomplete
              onLoad={(auto) => (autocompleteRef.current = auto)}
              onPlaceChanged={onPlaceChanged}
              options={{
                componentRestrictions: { country: "gb" },
                fields: ["geometry", "formatted_address"],
              }}
            >
              <Form.Control
                placeholder="Type UK address..."
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setError("");
                }}
              />
            </Autocomplete>

            <div className="pt-2">
              <GoogleMap 
              mapContainerStyle={mapContainerStyle}
              center={marker}
              zoom={15}
              onLoad={onMapLoad}
              onClick={handleMapClick}
            >
              <MarkerF
                position={marker}
                draggable
                animation={isBouncing ? window.google.maps.Animation.BOUNCE : undefined}
                onDragEnd={handleMarkerDragEnd}
              />
            </GoogleMap>
            </div>

            <Form.Label className="pt-2">Tags:</Form.Label>
            <Form.Control
              className="mt-2"
              placeholder="Tag (Home, Office etc)"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            />
          </>
        )}

        {!isLoaded && <p>Loading map...</p>}

        {error && <div className="text-danger mt-2">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleAddressUpdate} disabled={!isLoaded}>
          Update Address
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressUpdateModal;
